import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { WashingMachine, Wind, Package, Sparkle, Bed, Tag, Truck } from "@phosphor-icons/react";
import api from "@/lib/api";

const icons = { wash: WashingMachine, dry: Wind, fold: Package };
const catMeta = {
  bulky: { label: "Bulky Items", note: "Wash · Dry · Fold", Icon: Bed },
  iron: { label: "Ironing", note: "Per item", Icon: Sparkle },
  steam: { label: "Steaming", note: "Per item", Icon: Wind },
};

export default function Services() {
  const [data, setData] = useState(null);

  useEffect(() => {
    document.title = "Services & Pricing | Zaekids Laundromat — Laundry Pick Up & Delivery NSW";
    api.get("/services").then((r) => setData(r.data));
  }, []);

  if (!data) return <div className="py-32 text-center font-bold text-brand-green">Loading fresh prices…</div>;

  const wdf = data.bundles.find((b) => b.code === "wash_dry_fold");
  const minTotal = (data.min_weight_kg * (wdf?.price || 6)).toFixed(0);

  return (
    <div data-testid="services-page" className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
      <div className="text-center">
        <span className="font-script text-3xl font-bold text-brand-yellow-dark">Services & Pricing</span>
        <h1 className="mt-2 text-4xl font-black tracking-tight text-brand-green sm:text-5xl">Simple, honest pricing</h1>
        <p className="mx-auto mt-4 max-w-2xl text-slate-600">
          Wash · Dry · Fold is just <strong className="text-brand-green">${(wdf?.price || 6).toFixed(2)}/kg</strong> with a {data.min_weight_kg} kg minimum (${minTotal}).
          Bulky items, ironing and steaming are priced per item. FREE pick up & delivery within 10 km of Hebersham on orders over ${data.free_delivery_over.toFixed(0)}.
        </p>
      </div>

      {/* Wash Dry Fold highlight */}
      <div className="mt-12 grid gap-6 md:grid-cols-2">
        <div data-testid="wdf-card" className="card-lift relative overflow-hidden rounded-3xl border-4 border-brand-yellow bg-soft-yellow p-8">
          <h2 className="text-2xl font-extrabold text-brand-green">Wash · Dry · Fold</h2>
          <p className="mt-2 text-sm text-slate-600">Our most popular service — everyday laundry washed, dried and neatly folded.</p>
          <div className="mt-5 flex flex-wrap items-end gap-6">
            <div>
              <p className="text-xs font-bold uppercase text-slate-500">Up to 5 kg (minimum)</p>
              <p className="text-3xl font-black text-brand-green">${minTotal}</p>
            </div>
            <div>
              <p className="text-xs font-bold uppercase text-slate-500">Over 5 kg</p>
              <p className="text-3xl font-black text-brand-green">${(wdf?.price || 6).toFixed(2)}<span className="text-base font-bold text-slate-500">/kg</span></p>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          {data.services.map((s, i) => {
            const Icon = icons[s.code] || WashingMachine;
            return (
              <div key={s.code} data-testid={`pricing-card-${s.code}`}
                className="card-lift flex flex-col items-center rounded-3xl bg-white p-5 text-center shadow-[0_8px_30px_rgb(27,94,32,0.08)]">
                <span className={`flex h-14 w-14 items-center justify-center rounded-full ${i % 2 ? "bg-brand-yellow text-brand-green" : "bg-brand-green text-white"}`}>
                  <Icon size={28} weight="duotone" />
                </span>
                <h3 className="mt-3 font-extrabold text-brand-green">{s.name}</h3>
                <p className="mt-1 text-lg font-black text-brand-green">${s.price.toFixed(2)}<span className="text-xs font-bold text-slate-500">/kg</span></p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Add-on price lists */}
      <div className="mt-14 grid gap-8 lg:grid-cols-3">
        {["bulky", "iron", "steam"].map((cat) => {
          const meta = catMeta[cat];
          const items = data.addons?.[cat] || [];
          if (!items.length) return null;
          return (
            <div key={cat} data-testid={`addon-list-${cat}`} className="rounded-3xl bg-white p-6 shadow-[0_8px_30px_rgb(27,94,32,0.08)]">
              <div className="flex items-center gap-3">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-green text-white">
                  <meta.Icon size={24} weight="duotone" />
                </span>
                <div>
                  <h2 className="text-xl font-extrabold text-brand-green">{meta.label}</h2>
                  <p className="text-xs font-bold text-slate-400">{meta.note}</p>
                </div>
              </div>
              <ul className="mt-5 space-y-2">
                {items.map((i) => (
                  <li key={i.code} className="flex items-center justify-between border-b border-slate-100 pb-2 text-sm">
                    <span className="font-semibold text-slate-700">{i.name}</span>
                    <span className="font-black text-brand-green">
                      {i.price_max ? `from $${i.price.toFixed(2)}` : `$${i.price.toFixed(2)}`}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      {/* Pick up & delivery */}
      <div data-testid="delivery-tiers-card" className="mt-16 overflow-hidden rounded-3xl bg-brand-green p-8 text-white sm:p-10">
        <div className="flex items-center gap-3">
          <Truck size={36} weight="duotone" className="text-brand-yellow" />
          <h2 className="text-2xl font-extrabold">Pick Up & Delivery</h2>
        </div>
        <p className="mt-3 max-w-2xl text-green-50">
          <strong className="text-brand-yellow">$5.95</strong> within 10 km of Hebersham — or <strong className="text-brand-yellow">FREE</strong> within 10 km when your order is over ${data.free_delivery_over.toFixed(0)}. Further out, a small distance fee applies.
        </p>
        <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {[
            ["Within 10 km from Hebersham", "$5.95"],
            ["11 – 15 km", "+ $10"],
            ["16 – 20 km", "+ $15"],
            ["21 – 25 km", "+ $20"],
            ["Over 25 km", "Custom quote"],
          ].map(([label, price]) => (
            <div key={label} className="flex items-center justify-between rounded-2xl bg-white/10 px-4 py-3 text-sm">
              <span className="font-semibold text-green-50">{label}</span>
              <span className="font-black text-brand-yellow">{price}</span>
            </div>
          ))}
        </div>
        <Link to="/book" data-testid="pricing-book-btn" className="btn-bounce mt-8 inline-flex rounded-full bg-brand-yellow px-8 py-4 font-extrabold text-brand-green">
          Book a Pick Up
        </Link>
      </div>
    </div>
  );
}
